import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Star, 
  MapPin, 
  Clock, 
  Shield, 
  Car, 
  Zap,
  ChevronLeft,
  ChevronRight,
  User,
  Loader2,
  Calendar as CalendarIcon,
  Ban,
  MessageCircle
} from "lucide-react";

import { API } from "../utils/api";
import { trackViewContent, trackInitiateCheckout } from "../utils/metaPixel";
import TimePicker from "../components/TimePicker";
import BookingMessages from "../components/BookingMessages";

// Precio por día: price_day, o price_hour×8 (o mensual/30 si sale más barato)
const dayRate = (spot) => {
  if (spot.price_day) return spot.price_day;
  const rates = [spot.price_hour * 8];
  if (spot.price_month) rates.push(spot.price_month / 30);
  return Math.min(...rates);
};

export default function SpotDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [spot, setSpot] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [bookedDates, setBookedDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  
  const [bookingData, setBookingData] = useState({
    bookingType: "hour",
    startDate: new Date(),
    endDate: new Date(),
    startTime: "09:00",
    endTime: "18:00",
    vehiclePlate: "",
    notes: "",
    dni: ""
  });

  const fetchSpotData = useCallback(async () => {
    try {
      const [spotRes, reviewsRes, bookingsRes, bookedDatesRes] = await Promise.all([
        axios.get(`${API}/spots/${id}`),
        axios.get(`${API}/reviews/spot/${id}`),
        axios.get(`${API}/bookings/spot/${id}`),
        axios.get(`${API}/spots/${id}/booked-dates`)
      ]);
      setSpot(spotRes.data);
      trackViewContent(spotRes.data.id, spotRes.data.title, spotRes.data.price_hour);
      setReviews(reviewsRes.data);
      setBookings(bookingsRes.data);
      // Convertir strings de fecha a objetos Date para el calendario
      setBookedDates(bookedDatesRes.data.booked_dates.map(d => new Date(d + "T00:00:00")));
    } catch (error) {
      console.error("Error fetching spot:", error);
      toast.error("Plaza no encontrada");
      navigate("/explore");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchSpotData();
  }, [fetchSpotData]);

  // Función para verificar si una fecha está reservada
  const isDateBooked = (date) => {
    return bookedDates.some(bookedDate => 
      bookedDate.getFullYear() === date.getFullYear() &&
      bookedDate.getMonth() === date.getMonth() &&
      bookedDate.getDate() === date.getDate()
    );
  };

  // Combina una fecha (Date) con una hora "HH:MM" en un nuevo Date local
  const combineDateAndTime = (date, time) => {
    const [h, m] = (time || "00:00").split(":").map(Number);
    const d = new Date(date);
    d.setHours(h || 0, m || 0, 0, 0);
    return d;
  };

  // Devuelve las fechas finales a usar para la reserva (combinando hora si aplica)
  const getEffectiveDates = () => {
    if (bookingData.bookingType === "hour") {
      return {
        start: combineDateAndTime(bookingData.startDate, bookingData.startTime),
        end: combineDateAndTime(bookingData.endDate, bookingData.endTime),
      };
    }
    // Para "día" y "mes" usamos 00:00 inicio y 23:59 fin
    const start = new Date(bookingData.startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(bookingData.endDate);
    end.setHours(23, 59, 0, 0);
    return { start, end };
  };

  // Maneja cambio de tipo de reserva — ajusta automáticamente endDate según el tipo
  const handleBookingTypeChange = (newType) => {
    setBookingData((prev) => {
      const start = new Date(prev.startDate);
      let end = new Date(prev.startDate);
      if (newType === "day") {
        end.setDate(end.getDate() + 1);
      } else if (newType === "month") {
        end.setMonth(end.getMonth() + 1);
      } else {
        end = start; // hora: mismo día
      }
      return { ...prev, bookingType: newType, endDate: end };
    });
  };

  const handleBooking = async () => {
    if (!user) {
      toast.info("Necesitas iniciar sesión para reservar");
      navigate("/login");
      return;
    }

    setBookingLoading(true);
    try {
      const { start: effStart, end: effEnd } = getEffectiveDates();

      // Validación: fin debe ser estrictamente posterior al inicio
      if (effEnd <= effStart) {
        toast.error("La fecha/hora de fin debe ser posterior a la de inicio");
        setBookingLoading(false);
        return;
      }

      const payload = {
        parking_spot_id: id,
        start_date: effStart.toISOString(),
        end_date: effEnd.toISOString(),
        booking_type: bookingData.bookingType,
        vehicle_plate: bookingData.vehiclePlate || null,
        notes: bookingData.notes || null,
        dni: bookingData.dni || null
      };

      await axios.post(`${API}/bookings`, payload);
      // Nota: el evento `Purchase` de Meta Pixel se dispara únicamente desde
      // PaymentSuccess cuando Stripe confirma `payment_status === "paid"`.
      // Aquí enviamos `InitiateCheckout` que sí representa la intención de pago.
      const purchaseValue = parseFloat(calculatePrice());
      trackInitiateCheckout(purchaseValue, id, spot?.title);
      toast.success("¡Reserva confirmada! Te hemos enviado un email de confirmación.");
      setBookingModalOpen(false);
      navigate("/my-bookings");
    } catch (error) {
      const message = error.response?.data?.detail || "Error al crear la reserva";
      toast.error(message);
    } finally {
      setBookingLoading(false);
    }
  };

  const calculatePrice = () => {
    if (!spot) return 0;
    const { start, end } = getEffectiveDates();
    const diff = end - start;
    if (diff <= 0) return "0.00";
    
    if (bookingData.bookingType === "hour") {
      const hours = Math.max(1, diff / (1000 * 60 * 60));
      return (hours * spot.price_hour).toFixed(2);
    } else if (bookingData.bookingType === "day") {
      const days = Math.max(1, diff / (1000 * 60 * 60 * 24));
      return (days * dayRate(spot)).toFixed(2);
    } else {
      const months = Math.max(1, diff / (1000 * 60 * 60 * 24 * 30));
      return (months * (spot.price_month || spot.price_hour * 24 * 30)).toFixed(2);
    }
  };

  const getFeatureIcon = (feature) => {
    const icons = {
      "Cubierta": Shield,
      "Segura": Shield,
      "24h": Clock,
      "Vigilada": Shield,
      "Cámaras": Shield,
      "Cargador EV": Zap,
      "Fácil acceso": Car,
      "Portero": User
    };
    return icons[feature] || Shield;
  };

  const nextImage = () => {
    if (spot?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % spot.images.length);
    }
  };

  const prevImage = () => {
    if (spot?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + spot.images.length) % spot.images.length);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  if (!spot) return null;

  const defaultImage = "https://images.unsplash.com/photo-1548343361-02248be15911?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
  const images = spot.images?.length > 0 ? spot.images : [defaultImage];

  return (
    <div className="pt-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-5 h-5" />
          Volver
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images and Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <div className="relative gallery-main">
              <img
                src={images[currentImageIndex]}
                alt={spot.title}
                className="w-full h-full object-cover"
                data-testid="main-image"
              />
              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                    data-testid="prev-image-btn"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                    data-testid="next-image-btn"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {images.map((img, idx) => (
                      <button
                        key={`dot-${img || idx}`}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          idx === currentImageIndex ? "bg-white" : "bg-white/50"
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {images.map((img, idx) => (
                  <button
                    key={`thumb-${img || idx}`}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`gallery-thumb flex-shrink-0 w-20 h-20 ${
                      idx === currentImageIndex ? "active" : ""
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Details */}
            <div className="dashboard-card">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-2xl font-bold text-white mb-2" data-testid="spot-detail-title">
                    {spot.title}
                  </h1>
                  <div className="flex items-center gap-2 text-gray-400">
                    <MapPin className="w-4 h-4" />
                    <span>{spot.address}, {spot.city}</span>
                  </div>
                </div>
                {spot.rating > 0 && (
                  <div className="flex items-center gap-2 bg-amber-500/10 px-3 py-2 rounded-lg">
                    <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                    <span className="text-white font-semibold">{spot.rating.toFixed(1)}</span>
                    <span className="text-gray-400 text-sm">({spot.review_count} reseñas)</span>
                  </div>
                )}
              </div>

              <p className="text-gray-300 mb-6">{spot.description}</p>

              {/* Features */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-3">Características</h3>
                <div className="flex flex-wrap gap-3">
                  {spot.features?.map((feature, idx) => {
                    const Icon = getFeatureIcon(feature);
                    return (
                      <Badge key={idx} className="badge-feature flex items-center gap-2 py-2 px-4">
                        <Icon className="w-4 h-4" />
                        {feature}
                      </Badge>
                    );
                  })}
                </div>
              </div>

              {/* Dimensions */}
              {(spot.width || spot.length) && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white mb-3">Dimensiones</h3>
                  <div className="flex gap-6">
                    {spot.width && (
                      <div className="text-gray-400">
                        Ancho: <span className="text-white">{spot.width}m</span>
                      </div>
                    )}
                    {spot.length && (
                      <div className="text-gray-400">
                        Largo: <span className="text-white">{spot.length}m</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Owner */}
              <div className="pt-4 border-t border-white/5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                    <User className="w-6 h-6 text-violet-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{spot.owner_name || "Propietario"}</p>
                    <p className="text-sm text-gray-400">Propietario verificado</p>
                  </div>
                  {user && spot.owner_id !== user?.id && (
                    <button
                      onClick={() => setContactModalOpen(true)}
                      className="flex-shrink-0 h-10 px-3 sm:px-4 rounded-full bg-violet-500/15 hover:bg-violet-500/25 border border-violet-500/30 text-violet-300 hover:text-white text-sm font-medium transition-all flex items-center gap-1.5"
                      title="Enviar un mensaje al anfitrión"
                      data-testid="contact-host-inline-btn"
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span className="hidden sm:inline">Contactar</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Reviews */}
            <div className="dashboard-card">
              <h3 className="text-lg font-semibold text-white mb-4">
                Reseñas ({reviews.length})
              </h3>
              {reviews.length > 0 ? (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.id} className="pb-4 border-b border-white/5 last:border-0">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                          <User className="w-5 h-5 text-violet-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{review.user_name}</p>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-4 h-4 ${
                                  i < review.rating
                                    ? "fill-amber-400 text-amber-400"
                                    : "text-gray-600"
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      {review.comment && (
                        <p className="text-gray-400 ml-13">{review.comment}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">Aún no hay reseñas</p>
              )}
            </div>
          </div>

          {/* Right Column - Booking Card */}
          <div className="lg:col-span-1">
            <div className="dashboard-card sticky top-24">
              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="price-display text-3xl text-white">€{spot.price_hour.toFixed(2)}</span>
                  <span className="text-gray-400">/hora</span>
                </div>
                {(spot.price_day || spot.price_month) && (
                  <p className="text-gray-400 text-sm mt-1" data-testid="spot-secondary-prices">
                    {[
                      spot.price_day && `€${spot.price_day.toFixed(2)}/día`,
                      spot.price_month && `€${spot.price_month.toFixed(0)}/mes`,
                    ].filter(Boolean).join(" · ")}
                  </p>
                )}
              </div>

              <Button
                className="w-full h-14 bg-violet-500 hover:bg-violet-600 text-white text-lg glow-violet-hover disabled:opacity-60"
                onClick={() => user ? setBookingModalOpen(true) : navigate("/login")}
                disabled={spot.owner_id === user?.id || spot.host_can_receive_payments === false}
                data-testid="spot-book-now-btn"
              >
                {spot.owner_id === user?.id
                  ? "Es tu plaza"
                  : spot.host_can_receive_payments === false
                  ? "Reservas no disponibles"
                  : "Reservar ahora"}
              </Button>

              {spot.host_can_receive_payments === false ? (
                <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-500/30 p-3">
                  <span className="text-amber-400 text-lg leading-none">⚠️</span>
                  <p className="text-amber-100 text-xs leading-relaxed">
                    Este anfitrión aún no ha configurado sus cobros con Stripe. Las reservas estarán disponibles cuando lo complete.
                  </p>
                </div>
              ) : (
                <p className="text-center text-gray-500 text-sm mt-4">
                  No se cobrará nada ahora
                </p>
              )}

              {/* Quick info */}
              <div className="mt-6 pt-6 border-t border-white/5 space-y-3">
                <div className="flex items-center gap-3 text-gray-400">
                  <Clock className="w-5 h-5" />
                  <span>Disponible 24/7</span>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <Shield className="w-5 h-5" />
                  <span>Pago seguro garantizado</span>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <Car className="w-5 h-5" />
                  <span>Apto para: {spot.vehicle_types?.join(", ") || "coches"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <Dialog open={bookingModalOpen} onOpenChange={setBookingModalOpen}>
        <DialogContent className="bg-[#231f30] border-white/10 sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-violet-400" />
              Reservar Plaza
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Spot Summary */}
            <div className="bg-[#15121e] rounded-lg p-4 border border-white/10">
              <h4 className="text-white font-medium">{spot?.title}</h4>
              <p className="text-gray-400 text-sm flex items-center gap-1 mt-1">
                <MapPin className="w-4 h-4" />
                {spot?.address}, {spot?.city}
              </p>
            </div>

            {/* Booking Type */}
            <div>
              <Label className="text-gray-300 mb-2 block">Tipo de reserva</Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: "hour", label: "Por horas", price: spot?.price_hour },
                  { value: "day", label: "Por días", price: spot ? dayRate(spot) : 0 },
                  { value: "month", label: "Por meses", price: spot?.price_month || (spot?.price_hour * 24 * 30) }
                ].map((type) => (
                  <button
                    key={type.value}
                    onClick={() => handleBookingTypeChange(type.value)}
                    className={`p-3 rounded-lg border transition-all ${
                      bookingData.bookingType === type.value
                        ? "bg-violet-500/20 border-violet-500 text-white"
                        : "bg-[#15121e] border-white/10 text-gray-400 hover:border-white/20"
                    }`}
                    data-testid={`booking-type-${type.value}`}
                  >
                    <span className="block font-medium">{type.label}</span>
                    <span className="text-sm text-violet-400">{type.price?.toFixed(2)}€</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Booked dates legend */}
            {bookedDates.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-gray-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2">
                <Ban className="w-4 h-4 text-red-400" />
                <span>Las fechas en rojo ya están reservadas</span>
              </div>
            )}

            {/* Date Selection - Simplified */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-[#15121e] rounded-lg p-4 border border-white/10">
                <Label className="text-gray-300 mb-3 block text-center font-medium">Fecha inicio</Label>
                <Calendar
                  mode="single"
                  selected={bookingData.startDate}
                  onSelect={(date) => date && setBookingData(prev => {
                    let newEnd = prev.endDate;
                    if (prev.bookingType === "day") {
                      newEnd = new Date(date);
                      newEnd.setDate(newEnd.getDate() + 1);
                    } else if (prev.bookingType === "month") {
                      newEnd = new Date(date);
                      newEnd.setMonth(newEnd.getMonth() + 1);
                    } else if (prev.bookingType === "hour") {
                      // Para horas, fin = mismo día que inicio
                      newEnd = new Date(date);
                    } else if (date > prev.endDate) {
                      newEnd = date;
                    }
                    return { ...prev, startDate: date, endDate: newEnd };
                  })}
                  disabled={(date) => date < new Date() || isDateBooked(date)}
                  modifiers={{
                    booked: bookedDates
                  }}
                  modifiersStyles={{
                    booked: { 
                      backgroundColor: 'rgba(239, 68, 68, 0.3)',
                      color: '#ef4444',
                      textDecoration: 'line-through'
                    }
                  }}
                  className="mx-auto"
                  data-testid="start-date-calendar"
                />
              </div>
              <div className="bg-[#15121e] rounded-lg p-4 border border-white/10">
                <Label className="text-gray-300 mb-3 block text-center font-medium">Fecha fin</Label>
                <Calendar
                  mode="single"
                  selected={bookingData.endDate}
                  onSelect={(date) => date && setBookingData(prev => ({ ...prev, endDate: date }))}
                  disabled={(date) => date < bookingData.startDate || isDateBooked(date)}
                  modifiers={{
                    booked: bookedDates
                  }}
                  modifiersStyles={{
                    booked: { 
                      backgroundColor: 'rgba(239, 68, 68, 0.3)',
                      color: '#ef4444',
                      textDecoration: 'line-through'
                    }
                  }}
                  className="mx-auto"
                  data-testid="end-date-calendar"
                />
              </div>
            </div>

            {/* Time Pickers - Only visible for hour bookings */}
            {bookingData.bookingType === "hour" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <TimePicker
                  label="Hora de inicio"
                  value={bookingData.startTime}
                  onChange={(v) => setBookingData(prev => ({ ...prev, startTime: v }))}
                  accent="violet"
                  testId="start-time"
                />
                <TimePicker
                  label="Hora de fin"
                  value={bookingData.endTime}
                  onChange={(v) => setBookingData(prev => ({ ...prev, endTime: v }))}
                  accent="purple"
                  testId="end-time"
                />
              </div>
            )}

            {/* DNI Field - Only if required by spot */}
            {spot?.requires_dni && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <Label className="text-amber-400 mb-2 block font-medium flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  DNI / NIE (requerido)
                </Label>
                <Input
                  type="text"
                  placeholder="Ej: 12345678A"
                  value={bookingData.dni}
                  onChange={(e) => setBookingData(prev => ({ ...prev, dni: e.target.value.toUpperCase() }))}
                  className="bg-[#15121e] border-amber-500/30 text-white placeholder:text-gray-500 uppercase"
                  data-testid="dni-input"
                  required
                />
                <p className="text-amber-400/70 text-xs mt-2">
                  El propietario requiere identificación para esta reserva
                </p>
              </div>
            )}

            {/* Vehicle Plate & Notes - Side by side */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300 mb-2 block">Matrícula (opcional)</Label>
                <Input
                  type="text"
                  placeholder="Ej: 1234 ABC"
                  value={bookingData.vehiclePlate}
                  onChange={(e) => setBookingData(prev => ({ ...prev, vehiclePlate: e.target.value }))}
                  className="bg-[#15121e] border-white/10 text-white placeholder:text-gray-500"
                  data-testid="vehicle-plate-input"
                />
              </div>
              <div>
                <Label className="text-gray-300 mb-2 block">Notas (opcional)</Label>
                <Input
                  type="text"
                  placeholder="Información adicional..."
                  value={bookingData.notes}
                  onChange={(e) => setBookingData(prev => ({ ...prev, notes: e.target.value }))}
                  className="bg-[#15121e] border-white/10 text-white placeholder:text-gray-500"
                  data-testid="booking-notes-input"
                />
              </div>
            </div>

            {/* Price Summary con desglose de comisión Plazafy (10%) */}
            <div className="bg-gradient-to-br from-violet-500/15 to-purple-500/10 rounded-2xl p-5 border border-violet-500/25 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-300">
                  Plaza
                  <span className="text-gray-500 ml-2">
                    ({bookingData.bookingType === "hour" ? "por horas" :
                      bookingData.bookingType === "day" ? "por días" : "por meses"})
                  </span>
                </span>
                <span className="text-white font-medium">€{calculatePrice()}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400 flex items-center gap-1.5">
                  Tasa de servicio Plazafy
                  <span className="text-[10px] text-gray-500 bg-white/5 px-1.5 py-0.5 rounded">10%</span>
                </span>
                <span className="text-gray-300 font-medium">€{(parseFloat(calculatePrice()) * 0.10).toFixed(2)}</span>
              </div>
              <div className="border-t border-white/10 pt-3 flex items-end justify-between">
                <div>
                  <span className="text-gray-300 text-sm">Total a pagar</span>
                  <p className="text-xs text-gray-500">Incluye todos los cargos</p>
                </div>
                <span className="price-display text-2xl sm:text-3xl font-bold text-white">€{(parseFloat(calculatePrice()) * 1.10).toFixed(2)}</span>
              </div>
            </div>

            <Button
              className="w-full h-12 bg-violet-500 hover:bg-violet-600 text-white font-medium text-lg glow-violet-hover"
              onClick={handleBooking}
              disabled={bookingLoading || (spot?.requires_dni && !bookingData.dni.trim())}
              data-testid="confirm-booking-btn"
            >
              {bookingLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                "Confirmar Reserva"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Contactar anfitrión (pre-reserva) */}
      <Dialog open={contactModalOpen} onOpenChange={setContactModalOpen}>
        <DialogContent className="bg-[#231f30] border-white/10 sm:max-w-[550px] max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-violet-400" />
              Contactar con el anfitrión
            </DialogTitle>
            <div className="mt-2 p-3 bg-[#15121e] rounded-lg border border-white/5">
              <p className="text-white font-medium text-sm">{spot?.title}</p>
              <p className="text-gray-400 text-xs mt-1">
                Pregunta al anfitrión antes de reservar. Tu mensaje se guarda y podrás continuarlo desde "Mensajes".
              </p>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-hidden mt-3">
            <BookingMessages
              spotId={spot?.id}
              ownerName={spot?.owner_name || "el anfitrión"}
              contactMode
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
